import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie } from "lucide-react";

const CookiesPage = () => {
const { i18n, t } = useTranslation();
const showEnDisclaimer = (i18n.language || "").startsWith("en");
return ( <div className="min-h-screen py-20 px-6"> <div className="container mx-auto max-w-4xl">
{/* Header */} <div className="mb-12"> <div className="flex items-center gap-3 mb-6"> <Cookie className="w-10 h-10 text-gold" />


        <h1 className="font-display text-4xl md:text-5xl text-gold">
          {showEnDisclaimer ? "Cookie Policy" : "Cookie Policy"}
        </h1>
      </div>

      <p className="text-muted-foreground">
        {showEnDisclaimer ? "Last updated: 08/11/2026" : "Ultimo aggiornamento: 11/08/2026"}
      </p>

      {showEnDisclaimer && (
        <div className="mt-6 p-4 rune-border bg-gold/10 border border-gold rounded-lg" data-testid="legal-en-notice">
          <p className="font-heading text-sm text-gold mb-2">
            {t("legal.onlyItalianTitle")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("legal.onlyItalianBody")}
          </p>
        </div>
      )}
    </div>

    <div className="space-y-8 text-muted-foreground leading-relaxed">
      {/* 1 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          1. Cosa Sono i Cookie
        </h2>

        <p>
          I cookie sono piccoli file di testo e altri strumenti tecnici
          che un sito web può memorizzare sul dispositivo dell'utente
          o utilizzare durante la navigazione.
        </p>

        <p className="mt-3">
          Possono essere utilizzati, ad esempio, per mantenere una
          sessione autenticata, ricordare preferenze, gestire un
          carrello o garantire la sicurezza del servizio.
        </p>

        <p className="mt-3">
          Mytharion utilizza principalmente strumenti strettamente
          necessari al funzionamento e alla sicurezza della piattaforma.
          Non utilizziamo cookie per pubblicità comportamentale o
          profilazione degli utenti, salvo eventuali future modifiche
          comunicate e gestite secondo la normativa applicabile.
        </p>
      </section>

      {/* 2 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          2. Cookie e Strumenti Necessari
        </h2>

        <div className="space-y-6 mt-4">
          <div className="rune-border bg-card/50 p-6 rounded-lg">
            <h3 className="font-heading text-lg text-gold mb-3">
              Cookie Essenziali
            </h3>

            <p className="mb-3">
              Questi strumenti sono utilizzati esclusivamente nella
              misura necessaria per fornire funzionalità richieste
              dall'utente, mantenere la sicurezza del servizio e
              consentire il corretto funzionamento della piattaforma.
            </p>

            <ul className="list-disc pl-6 space-y-3 text-sm">
              <li>
                <strong>session_token</strong> - Cookie di autenticazione
                <br />
                <span className="text-xs text-muted-foreground">
                  Utilizzato per mantenere la sessione dell'utente
                  autenticato. Può essere configurato con attributi
                  di sicurezza quali HttpOnly, Secure e SameSite.
                </span>
              </li>

              <li>
                <strong>CSRF token</strong> - Protezione della sessione
                <br />
                <span className="text-xs text-muted-foreground">
                  Utilizzato, quando previsto dall'implementazione,
                  per proteggere le richieste da attacchi
                  Cross-Site Request Forgery.
                </span>
              </li>

              <li>
                <strong>Cookie di preferenza o funzionalità</strong>
                <br />
                <span className="text-xs text-muted-foreground">
                  Eventuali cookie necessari per ricordare impostazioni
                  o preferenze indispensabili al funzionamento del
                  Servizio.
                </span>
              </li>
            </ul>
          </div>

          <div className="border border-border p-6 rounded-lg opacity-60">
            <h3 className="font-heading text-lg text-muted-foreground mb-3">
              Cookie Analytics
            </h3>

            <p className="text-sm">
              Attualmente Mytharion non utilizza cookie analytics
              per finalità di profilazione o pubblicità.
            </p>

            <p className="text-sm mt-3">
              Qualora venissero introdotti strumenti analytics che
              richiedano il consenso dell'utente, questa Cookie Policy
              verrà aggiornata e il relativo consenso sarà richiesto
              prima dell'attivazione dello strumento.
            </p>
          </div>

          <div className="border border-border p-6 rounded-lg opacity-60">
            <h3 className="font-heading text-lg text-muted-foreground mb-3">
              Cookie di Marketing e Profilazione
            </h3>

            <p className="text-sm">
              Mytharion non utilizza cookie di profilazione o cookie
              pubblicitari per mostrare pubblicità personalizzata
              sulla base della navigazione dell'utente.
            </p>
          </div>
        </div>
      </section>

      {/* 3 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          3. Servizi di Terze Parti
        </h2>

        <p className="mb-4">
          Mytharion utilizza alcuni servizi forniti da soggetti terzi.
          A seconda della modalità tecnica con cui tali servizi sono
          integrati nel sito, possono essere utilizzati cookie o altri
          strumenti tecnici necessari alla loro erogazione.
        </p>

        <div className="space-y-4">
          {/* Cloudflare */}
          <div className="border border-border bg-card/30 p-5 rounded-lg">
            <h4 className="font-heading text-foreground mb-2">
              Cloudflare
            </h4>

            <p className="text-sm mb-2">
              Cloudflare può essere utilizzato per servizi di sicurezza,
              rete, protezione da traffico dannoso e distribuzione dei
              contenuti. Alcuni strumenti di Cloudflare possono essere
              necessari per garantire sicurezza e affidabilità del sito.
            </p>

            <a
              href="https://www.cloudflare.com/privacypolicy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold hover:underline text-sm"
            >
              Privacy Policy Cloudflare →
            </a>
          </div>

          {/* Stripe */}
          <div className="border border-purple-500/30 bg-purple-900/10 p-5 rounded-lg">
            <h4 className="font-heading text-foreground mb-2">
              Stripe
            </h4>

            <p className="text-sm mb-2">
              Stripe viene utilizzato per elaborare pagamenti e
              supportare funzionalità relative alle transazioni.
              Durante le procedure gestite tramite Stripe possono
              essere utilizzati cookie o altri strumenti tecnici e
              di sicurezza secondo l'integrazione utilizzata.
            </p>

            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-400 hover:underline text-sm"
            >
              Privacy Policy Stripe →
            </a>
          </div>

          {/* PayPal */}
          <div className="border border-blue-500/30 bg-blue-900/10 p-5 rounded-lg">
            <h4 className="font-heading text-foreground mb-2">
              PayPal
            </h4>

            <p className="text-sm mb-2">
              PayPal viene utilizzato come metodo di pagamento.
              Durante l'utilizzo dei servizi PayPal possono essere
              utilizzati cookie o altri strumenti tecnici necessari
              per sicurezza, autenticazione, prevenzione delle frodi
              ed elaborazione del pagamento.
            </p>

            <a
              href="https://www.paypal.com/it/legalhub/privacy-full"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline text-sm"
            >
              Privacy Policy PayPal →
            </a>
          </div>

          {/* Google */}
          <div className="border border-gold/30 bg-gold/5 p-5 rounded-lg">
            <h4 className="font-heading text-foreground mb-2">
              Google OAuth
            </h4>

            <p className="text-sm mb-2">
              Se scegli di autenticarti tramite Google, il processo
              di autenticazione può comportare l'utilizzo di cookie
              o altri strumenti tecnici gestiti da Google secondo
              le modalità previste dal relativo servizio.
            </p>

            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold hover:underline text-sm"
            >
              Privacy Policy Google →
            </a>
          </div>
        </div>

        <p className="mt-4 text-sm">
          I servizi di terze parti possono trattare dati secondo le
          rispettive informative e condizioni. L'utilizzo di tali
          servizi non significa che Mytharion venda i dati personali
          degli utenti.
        </p>
      </section>

      {/* 4 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          4. Local Storage e Tecnologie Simili
        </h2>

        <p className="mb-4">
          Oltre ai cookie, il sito può utilizzare il Local Storage
          o tecnologie analoghe del browser per memorizzare dati
          strettamente necessari al funzionamento dell'interfaccia
          e delle funzionalità richieste dall'utente.
        </p>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-gold">•</span>

            <div>
              <p className="text-sm">
                Il Local Storage può essere utilizzato per salvare
                preferenze dell'interfaccia o informazioni necessarie
                al funzionamento del Servizio.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-gold">•</span>

            <div>
              <p className="font-semibold text-foreground">
                Nessuna finalità pubblicitaria
              </p>

              <p className="text-sm">
                Mytharion non utilizza il Local Storage per creare
                profili pubblicitari degli utenti.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          5. Consenso e Gestione delle Preferenze
        </h2>

        <p className="mb-4">
          I cookie e gli strumenti strettamente necessari al
          funzionamento del Servizio possono essere utilizzati
          senza un consenso preventivo quando rientrano nelle
          condizioni previste dalla normativa applicabile.
        </p>

        <p className="mb-4">
          Qualora Mytharion introduca cookie o altri strumenti
          di tracciamento che richiedano il consenso, tali strumenti
          non saranno attivati prima dell'acquisizione del consenso
          richiesto dalla legge.
        </p>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-gold">•</span>

            <div>
              <p className="font-semibold text-foreground">
                Impostazioni del browser
              </p>

              <p className="text-sm">
                Puoi bloccare o eliminare i cookie tramite le
                impostazioni del browser. La disattivazione dei
                cookie essenziali può impedire il corretto
                funzionamento di alcune parti del Servizio.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-gold">•</span>

            <div>
              <p className="font-semibold text-foreground">
                Logout
              </p>

              <p className="text-sm">
                Effettuando il logout, le informazioni di autenticazione
                della sessione possono essere invalidate o eliminate
                secondo il funzionamento tecnico del Servizio.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-gold">•</span>

            <div>
              <p className="font-semibold text-foreground">
                Navigazione privata
              </p>

              <p className="text-sm">
                Le modalità di navigazione privata del browser possono
                limitare la conservazione locale di cookie e dati
                di navigazione, secondo le impostazioni del browser.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          6. Durata dei Cookie
        </h2>

        <p className="mb-4">
          La durata dei cookie dipende dalla loro funzione e dalla
          configurazione tecnica del Servizio.
        </p>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Cookie di sessione:</strong> possono essere
            eliminati alla chiusura della sessione o del browser,
            secondo la loro configurazione.
          </li>

          <li>
            <strong>Cookie persistenti:</strong> rimangono memorizzati
            per il periodo stabilito dal relativo cookie.
          </li>

          <li>
            <strong>Cookie di terze parti:</strong> la durata può
            essere determinata dal rispettivo fornitore.
          </li>
        </ul>

        <p className="mt-4 text-sm">
          La durata effettiva dei singoli cookie può variare in base
          agli aggiornamenti tecnici dei servizi utilizzati.
        </p>
      </section>

      {/* 7 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          7. Cookie e Protezione dei Dati
        </h2>

        <p>
          Alcuni cookie o identificatori possono comportare il
          trattamento di dati personali, ad esempio quando sono
          associati a un account o a un identificativo online.
        </p>

        <p className="mt-3">
          Il trattamento dei dati personali connesso all'utilizzo
          del sito è descritto in modo più completo nella nostra{" "}
          <Link
            to="/privacy"
            className="text-gold hover:underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      {/* 8 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          8. Aggiornamenti a Questa Policy
        </h2>

        <p>
          Questa Cookie Policy può essere aggiornata per riflettere
          modifiche normative, tecniche o funzionali della piattaforma,
          nonché l'introduzione o la modifica di servizi di terze parti.
        </p>

        <p className="mt-3">
          In caso di introduzione di cookie o strumenti di tracciamento
          che richiedano il consenso dell'utente, saranno adottate
          le misure necessarie per ottenere il consenso prima della
          loro attivazione, quando richiesto dalla normativa applicabile.
        </p>

        <p className="mt-3">
          La data dell'ultimo aggiornamento è indicata nella parte
          superiore di questa pagina.
        </p>
      </section>

      {/* 9 */}
      <section>
        <h2 className="font-heading text-2xl text-foreground mb-4">
          9. Contatti
        </h2>

        <p>
          Per domande relative ai cookie, agli strumenti di
          tracciamento o alla presente Cookie Policy puoi contattarci:
        </p>

        <p className="mt-3">
          <strong>Email:</strong>{" "}
          <a
            href="mailto:privacy@mytharion.app"
            className="text-gold hover:underline"
          >
            privacy@mytharion.app
          </a>
        </p>
      </section>

      {/* Important Note */}
      <div className="mt-8 p-6 bg-gold/10 border border-gold/30 rounded-lg">
        <p className="text-sm text-foreground">
          <strong>📌 Nota Importante:</strong>{" "}
          Mytharion è un progetto indipendente che presta particolare
          attenzione alla protezione dei dati personali degli utenti.
          Utilizziamo gli strumenti di tracciamento nel rispetto delle
          finalità per cui sono necessari e non vendiamo i dati personali
          degli utenti per finalità pubblicitarie.
        </p>

        <p className="text-sm text-foreground mt-3">
          La presente Cookie Policy deve essere letta insieme alla{" "}
          <Link
            to="/privacy"
            className="text-gold hover:underline"
          >
            Privacy Policy
          </Link>{" "}
          e ai{" "}
          <Link
            to="/terms"
            className="text-gold hover:underline"
          >
            Termini di Servizio
          </Link>
          .
        </p>
      </div>
    </div>

    {/* Footer */}
    <div className="mt-12 pt-8 border-t border-border">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <Link
          to="/"
          className="text-gold hover:text-gold/80 text-sm"
        >
          ← Torna alla Home
        </Link>

        <div className="text-sm text-muted-foreground">
          Vedi anche:{" "}
          <Link
            to="/privacy"
            className="text-gold hover:underline"
          >
            Privacy Policy
          </Link>
          {" · "}
          <Link
            to="/terms"
            className="text-gold hover:underline"
          >
            Termini di Servizio
          </Link>
        </div>
      </div>
    </div>
  </div>
</div>

);
};

export default CookiesPage;
